import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Box, Stack, Typography, List, ListItemButton, ListItemIcon, ListItemText, Avatar, IconButton,
  Collapse, Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DashboardIcon from "@mui/icons-material/SpaceDashboardOutlined";
import PlayCircleIcon from "@mui/icons-material/PlayCircleOutline";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AssessmentIcon from "@mui/icons-material/AssessmentOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import SecurityIcon from "@mui/icons-material/Security";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { BRAND } from "../styles/theme";
import { useAuth, logout } from "../services/auth";
import { getBindStatus, BIND_STATUS_EVENT } from "../services/bind";

type NavLeaf  = { to: string; icon?: JSX.Element; label: string };
type NavGroup = { label: string; icon: JSX.Element; children: NavLeaf[] };
type NavEntry = NavLeaf | NavGroup;

// CP-482: menú agrupado por tarea (opción A). Las URLs no cambian; solo el orden,
// los grupos y los nombres. Transfers to Suppliers / Merchant (ComingSoon, sin
// migrar desde WA1) van al final, en "Pendientes de migración".
const NAV: NavEntry[] = [
  { to: "/monitoreo/dashboard", icon: <DashboardIcon />, label: "Inicio" },
  { label: "Operación", icon: <PlayCircleIcon />, children: [
    // CP-584: primero lo de collectors, DEBINes y saldos; después el resto.
    { to: "/collector",                label: "Collectors" },
    { to: "/collector-operation",      label: "Movimientos de collector" },
    { to: "/debines",                  label: "DEBINes" },
    { to: "/monitoreo/collectors",     label: "Estado de saldos" },
    { to: "/monitoreo/cobros",         label: "Cobros del día" },
    { to: "/monitoreo/transferencias", label: "Liquidaciones" },
    { to: "/onboarding",               label: "Altas nuevas" },
    { to: "/cmf/modificar-cvu",        label: "Modificar CVU (Coelsa)" },
  ] },
  { label: "Bancos y conciliación", icon: <AccountBalanceIcon />, children: [
    { to: "/bind/saldos",       label: "Saldos Bind" },
    { to: "/cmf/saldos",        label: "Saldos CMF" },
    { to: "/bind/movimientos",  label: "Extracto Bind" },
    { to: "/cmf/movimientos",   label: "Extracto CMF" },
    { to: "/bind/ag-extracto",  label: "AG-Extracto (Bind)" },
    { to: "/cmf/recupero",      label: "Recupero de webhooks" },
    { to: "/bind-status",       label: "Bind Status (aviso a clientes)" },
  ] },
  { label: "Soporte", icon: <SupportAgentIcon />, children: [
    { to: "/monitoreo/id-coelsa", label: "Rastreo por ID" },
    { to: "/monitoreo/buscador",  label: "Buscar alias / CVU" },
    { to: "/monitoreo/buscar-cuit", label: "Inteligencia por CUIT" },
  ] },
  { label: "Reportes", icon: <AssessmentIcon />, children: [
    { to: "/monitoreo/serie-mensual",  label: "Serie mensual" },
    { to: "/monitoreo/compliance-cmf", label: "Reportes CMF" },
    { to: "/collector-splits",         label: "Splits" },
  ] },
  // Facturación es un menú propio, separado de Reportes (pedido de Pablo, 21/10).
  { label: "Facturación", icon: <ReceiptLongIcon />, children: [
    { to: "/billing",            label: "Facturas" },
    // Ruta hermana (no /billing/…) para que el NavLink de Facturas no quede
    // activo a la vez, igual que /collector-splits.
    { to: "/billing-provincias", label: "Por provincia" },
  ] },
  { label: "Configuración", icon: <TuneIcon />, children: [
    { to: "/whitelist",         label: "Whitelist DEBIN" },
    { to: "/bind-certificates", label: "Certificados Bind" },
  ] },
  { label: "Sistema y seguridad", icon: <SecurityIcon />, children: [
    { to: "/monitoreo/enlaces", label: "Enlaces bancarios" },
    { to: "/monitoreo/proveedores", label: "Proveedores (APIs)" },
    { to: "/monitoreo/apis",    label: "APIs CMF / Coelsa" },
    { to: "/monitoreo/paises",  label: "Países (WAF)" },
    { to: "/monitoreo/ips",     label: "IPs (WAF)" },
    { to: "/monitoreo/dominio", label: "Dominios" },
    { to: "/access",            label: "Accesos" },
  ] },
  { label: "Pendientes de migración", icon: <PendingActionsIcon />, children: [
    { to: "/merchant",             label: "Merchant Management" },
    { to: "/transfers-management", label: "Transfers to Suppliers Management" },
    { to: "/check-movement",       label: "Check movement" },
  ] },
];

// Lista plana (hojas) para resolver el título del topbar.
const NAV_LEAVES: NavLeaf[] = NAV.flatMap((n) => ("children" in n ? n.children : [n]));

const SIDEBAR_W_OPEN    = 260;
const SIDEBAR_W_CLOSED  = 64;
const TOPBAR_H          = 56;
const BRAND_W           = SIDEBAR_W_OPEN;  // match open-sidebar width so edges line up
const LS_SIDEBAR_OPEN   = "webadmin2.sidebar.open";

export default function Layout() {
  const { email } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const currentPage = NAV_LEAVES.find((n) => (location.pathname === n.to || location.pathname.startsWith(n.to + "/")))?.label ?? "";
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState<boolean>(() => {
    const v = localStorage.getItem(LS_SIDEBAR_OPEN);
    return v === null ? true : v === "1";
  });
  // CP-523: indicador en la barra superior mientras el aviso de demoras de Bind
  // está activo para los clientes. El GET es anónimo y CoreDB lo cachea; no llama a Bind.
  const [bindAviso, setBindAviso] = useState(false);
  useEffect(() => {
    const check = () => { getBindStatus().then((s) => setBindAviso(s === 1)).catch(() => {}); };
    check();
    const id = setInterval(() => { if (document.visibilityState === "visible") check(); }, 120_000);
    const onChange = (e: Event) => setBindAviso((e as CustomEvent<number>).detail === 1);
    window.addEventListener(BIND_STATUS_EVENT, onChange);
    return () => { clearInterval(id); window.removeEventListener(BIND_STATUS_EVENT, onChange); };
  }, []);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(LS_SIDEBAR_OPEN, next ? "1" : "0");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Topbar — never collapses */}
      <Box sx={{
        height:       TOPBAR_H,
        display:      "flex",
        alignItems:   "center",
        bgcolor:      "#fff",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        boxShadow:    "0 1px 2px rgba(0,0,0,0.04)",
        position:     "sticky",
        top:          0,
        zIndex:       20,
        pr:           2,
      }}>
        {/* Brand block — FIXED width, violet background, always visible */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{
          width:    BRAND_W,
          minWidth: BRAND_W,
          height:   "100%",
          bgcolor:  BRAND.sidebarBg,
          color:    "#fff",
          pl:       2.5,
        }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 26, flexShrink: 0 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 18, whiteSpace: "nowrap", letterSpacing: 0.2 }}>
            Web Admin v2
          </Typography>
        </Stack>

        {/* Collapse toggle + page title separator */}
        <IconButton
          onClick={toggle}
          size="small"
          sx={{
            ml:    1,
            color: BRAND.primary,
            "&:hover": { bgcolor: "rgba(91,47,235,0.08)" },
          }}
          aria-label={open ? "collapse sidebar" : "expand sidebar"}
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>

        <Typography sx={{
          color:      "rgba(0,0,0,0.2)",
          fontSize:   24,
          fontWeight: 300,
          mx:         1.5,
          userSelect: "none",
        }}>|</Typography>

        <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#222", whiteSpace: "nowrap" }}>
          {currentPage}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {bindAviso && (
          <Chip icon={<WarningAmberIcon />} label="Aviso de demoras Bind ACTIVO" color="warning" size="small"
            onClick={() => nav("/bind-status")} sx={{ mr: 1.5, fontWeight: 700, cursor: "pointer" }} />
        )}

        {/* User chip on the right */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{
          bgcolor:      "rgba(91,47,235,0.06)",
          border:       `1px solid rgba(91,47,235,0.15)`,
          borderRadius: 999,
          px:           1.25,
          py:           0.5,
        }}>
          <Avatar sx={{ bgcolor: BRAND.primary, color: "#fff", width: 28, height: 28, fontSize: 13, fontWeight: 700 }}>
            {email?.[0]?.toUpperCase() ?? "?"}
          </Avatar>
          <Typography variant="body2" sx={{ color: "#333", fontWeight: 500, pr: 0.5 }}>
            {email}
          </Typography>
        </Stack>
      </Box>

      {/* Main split: sidebar (nav only) + content */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box component="nav" sx={{
          width:      open ? SIDEBAR_W_OPEN : SIDEBAR_W_CLOSED,
          minWidth:   open ? SIDEBAR_W_OPEN : SIDEBAR_W_CLOSED,
          overflow:   "hidden",
          transition: "width .2s, min-width .2s",
          bgcolor:    BRAND.sidebarBg,
          color:      "#fff",
          display:    "flex",
          flexDirection: "column",
          py:         1.5,
          px:         1,
        }}>
          {/* Scrolleable: con los grupos expandidos el menú supera el alto de la
              pantalla; Log out queda fijo al fondo, fuera del scroll. */}
          <List dense sx={{
            flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.25)", borderRadius: 3 },
          }}>
            {(() => {
              const leafSx = {
                borderRadius:   2,
                mb:             0.5,
                color:          "#fff",
                justifyContent: open ? "flex-start" : "center",
                px:             open ? 2 : 1.5,
                py:             1.25,
                "&.active":     { bgcolor: "#fff", color: BRAND.primary },
                "&.active .MuiListItemIcon-root": { color: BRAND.primary },
                "& .MuiListItemIcon-root": { color: "#fff", minWidth: open ? 36 : 0 },
              } as const;
              return NAV.map((n) => {
                if ("children" in n) {
                  // Sidebar colapsado: el padre linkea al primer hijo (Operations).
                  if (!open) {
                    return (
                      <ListItemButton key={n.label} component={NavLink} to={n.children[0].to}
                        title={n.label} sx={leafSx}>
                        <ListItemIcon>{n.icon}</ListItemIcon>
                      </ListItemButton>
                    );
                  }
                  const childActive = n.children.some((c) => (location.pathname === c.to || location.pathname.startsWith(c.to + "/")));
                  const expanded = groupOpen[n.label] ?? childActive;
                  return (
                    <Box key={n.label}>
                      <ListItemButton
                        onClick={() => setGroupOpen((g) => ({ ...g, [n.label]: !(g[n.label] ?? childActive) }))}
                        sx={leafSx}
                      >
                        <ListItemIcon>{n.icon}</ListItemIcon>
                        <ListItemText primary={n.label} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                        {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                      </ListItemButton>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <List dense disablePadding>
                          {n.children.map((c) => (
                            <ListItemButton key={c.to} component={NavLink} to={c.to}
                              sx={{ ...leafSx, pl: 3.5, py: 0.75 }}>
                              <ListItemIcon sx={{ minWidth: 26 }}>
                                <FiberManualRecordIcon sx={{ fontSize: 8 }} />
                              </ListItemIcon>
                              <ListItemText primary={c.label} primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }} />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  );
                }
                return (
                  <ListItemButton key={n.to} component={NavLink} to={n.to}
                    title={open ? "" : n.label} sx={leafSx}>
                    <ListItemIcon>{n.icon}</ListItemIcon>
                    {open && <ListItemText primary={n.label} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />}
                  </ListItemButton>
                );
              });
            })()}
          </List>

          <List dense>
            <ListItemButton
              onClick={() => { logout(); nav("/login", { replace: true }); }}
              title={open ? "" : "Log out"}
              sx={{
                color:          "#fff",
                borderRadius:   2,
                justifyContent: open ? "flex-start" : "center",
                px:             open ? 2 : 1.5,
                py:             1.25,
                "& .MuiListItemIcon-root": { color: "#fff", minWidth: open ? 36 : 0 },
              }}
            >
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              {open && <ListItemText primary="Log out" />}
            </ListItemButton>
          </List>

          {/* Versión de la app — bumpeada por `deploy.sh` (npm version patch).
              Alineada a la izquierda como los items del menú. */}
          {open && (
            <Typography
              variant="caption"
              sx={{
                color:      "rgba(255,255,255,0.5)",
                pl:         2.5,
                pt:         0.5,
                pb:         0.5,
                fontSize:   11,
                userSelect: "none",
                display:    "block",
              }}
            >
              v{__APP_VERSION__}
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, bgcolor: "#fff", overflow: "auto" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
